import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import type { Taxonomy } from '@/types/api';

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  validFeatures: (typeId: string) => ['taxonomy', 'valid-features', typeId] as const,
  validStyles: (typeId: string) => ['taxonomy', 'valid-styles', typeId] as const,
  subtypes: (typeId: string) => ['taxonomy', 'subtypes', typeId] as const,
};

export function useTaxonomy() {
  return useQuery({
    queryKey: taxonomyKeys.all,
    queryFn: () => api.get<Taxonomy>('/api/v1/enrichment/taxonomy'),
    staleTime: 1000 * 60 * 30, // 30 minutes - taxonomy doesn't change often
  });
}

/**
 * Fetch valid feature IDs for a specific L1 location type.
 * Only features with these IDs can be synced to ScoutAround for this type.
 *
 * @param typeId - The L1 location type ID
 */
export function useValidFeatures(typeId: string | null) {
  return useQuery({
    queryKey: taxonomyKeys.validFeatures(typeId ?? ''),
    queryFn: () =>
      api.get<{ featureIds: string[] }>(
        `/api/v1/enrichment/taxonomy/valid-features?typeId=${typeId ?? ''}`
      ),
    enabled: Boolean(typeId),
    staleTime: 1000 * 60 * 30, // 30 minutes - valid relations don't change often
    select: (data) => data.featureIds,
  });
}

/**
 * Fetch valid style IDs for a specific L1 location type.
 * Only styles with these IDs can be synced to ScoutAround for this type.
 *
 * @param typeId - The L1 location type ID
 */
export function useValidStyles(typeId: string | null) {
  return useQuery({
    queryKey: taxonomyKeys.validStyles(typeId ?? ''),
    queryFn: () =>
      api.get<{ styleIds: string[] }>(
        `/api/v1/enrichment/taxonomy/valid-styles?typeId=${typeId ?? ''}`
      ),
    enabled: Boolean(typeId),
    staleTime: 1000 * 60 * 30, // 30 minutes - valid relations don't change often
    select: (data) => data.styleIds,
  });
}

/**
 * Fetch L2 subtype descriptors for a specific L1 location type.
 *
 * @param typeId - The L1 location type ID
 */
export function useSubtypes(typeId: string | null) {
  return useQuery({
    queryKey: taxonomyKeys.subtypes(typeId ?? ''),
    queryFn: () =>
      api.get<{ subtypes: { id: string; name: string; parentId: string }[] }>(
        `/api/v1/enrichment/taxonomy/subtypes?typeId=${typeId ?? ''}`
      ),
    enabled: Boolean(typeId),
    staleTime: 1000 * 60 * 30,
    select: (data) => data.subtypes,
  });
}
